import { XMarkIcon } from "@heroicons/react/24/outline";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { InfoWithLabelSkeleton } from "@/core/components/info-with-label-skeleton";
import { Button } from "@/core/components/ui/button";
import { Separator } from "@/core/components/ui/separator";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatDateAndTime } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { ISpiff } from "@/spiffs/spiffs.d";
import { SpiffEventBadge } from "./spiff-event-badge";

interface SpiffDetailSidebarHeaderProps {
  spiff?: ISpiff;
  onClose: () => void;
  onApprove?: () => void;
  onDismiss?: () => void;
  onReinstate?: () => void;
  isApproving?: boolean;
  isDismissing?: boolean;
  isReinstating?: boolean;
  isLoading?: boolean;
}

export const SpiffDetailSidebarHeader = ({
  spiff,
  isLoading,
  onClose,
  onApprove,
  onDismiss,
  onReinstate,
  isApproving,
  isDismissing,
  isReinstating,
}: SpiffDetailSidebarHeaderProps) => {
  if (!spiff || isLoading) {
    return (
      <Stack gap="md" fullWidth className="bg-bg-secondary/50 p-6">
        <Skeleton className="h-7 w-[128px]" />
        <Skeleton className="h-5 w-[120px]" />
        <Stack direction="row" gap="sm" fullWidth>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </Stack>
      </Stack>
    );
  }

  const { dismissed_at, trigger_event } = spiff;

  return (
    <Stack gap="md" fullWidth className="bg-bg-secondary/50 p-6">
      <Stack direction="row" justify="between" fullWidth>
        <Stack>
          <SpiffEventBadge triggerEvent={trigger_event} />
          <Typography variant="subtitleW1">
            {dismissed_at ? "Payment Dismissed" : "Payment Due"}
          </Typography>
        </Stack>
        <Button variant="unstyled" onClick={onClose}>
          <XMarkIcon className="size-6 text-secondary-text" />
        </Button>
      </Stack>

      {dismissed_at && onReinstate && (
        <Button
          variant="default"
          size="sm"
          onClick={onReinstate}
          loading={isReinstating}
          fullWidth
        >
          Reinstate
        </Button>
      )}

      {!dismissed_at && (onApprove || onDismiss) && (
        <Stack direction="row" gap="sm" fullWidth>
          {onDismiss && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onDismiss}
              loading={isDismissing}
              disabled={isApproving}
              className="flex-1"
            >
              Dismiss
            </Button>
          )}
          {onApprove && (
            <Button
              variant="default"
              size="sm"
              onClick={onApprove}
              loading={isApproving}
              disabled={isDismissing}
              className="flex-1"
            >
              Approve
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};

interface SpiffDetailSidebarInfoProps {
  technicianName: string;
  amount: number;
  submittedAt: string;
  isLoading?: boolean;
}

export const SpiffDetailSidebarInfo = ({
  technicianName,
  amount,
  submittedAt,
  isLoading = false,
}: SpiffDetailSidebarInfoProps) => {
  if (isLoading) {
    return (
      <>
        <Stack direction="row" gap="none" fullWidth>
          <InfoWithLabelSkeleton contentWidth="w-32" />
          <InfoWithLabelSkeleton contentWidth="w-20" />
        </Stack>
        <InfoWithLabelSkeleton contentWidth="w-40" />
      </>
    );
  }

  return (
    <>
      <Stack direction="row" gap="none" fullWidth>
        <InfoWithLabel label="Technician">{technicianName}</InfoWithLabel>
        <InfoWithLabel label="Amount">{formatMoney(amount)}</InfoWithLabel>
      </Stack>

      <InfoWithLabel label="Submitted at">
        {formatDateAndTime(submittedAt)}
      </InfoWithLabel>
    </>
  );
};

interface TriggerTypeLabelProps {
  label: string;
  isLoading?: boolean;
}

export const TriggerTypeLabel = ({
  label,
  isLoading = false,
}: TriggerTypeLabelProps) => {
  if (isLoading) {
    return (
      <>
        <Separator />
        <Skeleton className="h-7 w-32" />
      </>
    );
  }

  return (
    <>
      <Separator />
      <Typography variant="subtitleW2">{label}</Typography>
    </>
  );
};
